import { useEffect, useMemo, useState } from "react";
import type { PlayerLookupResult } from "../../shared/types";

/**
 * Which of a tournament's raw entrant names already resolve to a canonical
 * player page - used to make entrant names clickable in the catalog set
 * list, a tournament's Overview/Players tabs, and a player page's own set
 * list. Resolution is tournament-scoped (a raw display name isn't globally
 * unique across tournaments), so every caller passes the tournament its
 * names came from alongside the names themselves.
 */
export function usePlayerLookup(
  tournamentFolder: string | null | undefined,
  names: string[],
): PlayerLookupResult {
  const [result, setResult] = useState<PlayerLookupResult>({});
  // `names` is a fresh array every render; build the request URL from its
  // actual contents (serialized into a primitive key) so this only
  // recomputes, and the effect below only refetches, when the tournament or
  // the set of names actually changes.
  const namesKey = JSON.stringify(names);
  const url = useMemo(() => {
    if (!tournamentFolder || names.length === 0) return null;
    // Repeated `names` params (rather than one comma-joined value) so a raw
    // entrant name containing a literal comma can't be confused with the
    // separator between names.
    const params = new URLSearchParams({ tournament: tournamentFolder });
    for (const name of names) params.append("names", name);
    return `/api/players/lookup?${params}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentFolder, namesKey]);

  useEffect(() => {
    if (!url) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResult({});
      return;
    }
    let cancelled = false;
    fetch(url)
      .then((res) => (res.ok ? (res.json() as Promise<PlayerLookupResult>) : {}))
      .then((data: PlayerLookupResult) => {
        if (!cancelled) setResult(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [url]);

  return result;
}
